/**
 * New requests on the live map: which requests of a refresh were not there before (none on the first load, or when
 * the range or the search changes), the path one took over the map's arrows (from its steps, in order), and the
 * dots in flight (at most 3 at a time, at most 3 waiting, the rest dropped). Pure, tested in mapFlights.test.js;
 * ServiceMap draws them.
 */

export const MAX_FLIGHTS = 3;
export const MAX_WAITING = 3;
export const HOP_MS = 650;

/** The requests newest first (by start), as the list shows them. */
export function sortNewest(traces = []) {
  return [...traces].sort((a, b) => (b.start || 0) - (a.start || 0));
}

/**
 * The requests of a refresh not seen before: `{ fresh: traceId[], memory }`, `fresh` newest first and at most `cap`.
 * `memory` (`{ key, seen }`, start with null) is kept between refreshes; `key` says what is listed (range, search,
 * filter): a first load or another key shows nothing new, it only remembers what is there.
 */
export function pickNewTraces(memory, key, traces = [], cap = MAX_FLIGHTS) {
  const ids = traces.map((t) => t.traceId);
  if (!memory || memory.key !== key) {
    return { fresh: [], memory: { key, seen: new Set(ids) } };
  }
  const fresh = sortNewest(traces.filter((t) => !memory.seen.has(t.traceId))).slice(0, cap).map((t) => t.traceId);
  return { fresh, memory: { key, seen: new Set([...memory.seen, ...ids]) } };
}

/**
 * The arrows a request went along, in the order of its steps: `[{ edgeId, from, to, bad }]`. Each step comes from the
 * step that called it (a message's consumer: from the topic it waited in), along an arrow of the map; the first from
 * the Browser when the map has that arrow. Steps on the same box, or with no arrow on the map, are skipped.
 */
export function tracePath(trace, map) {
  const edges = new Set((map?.edges || []).map((e) => e.id));
  const has = (from, to) => from && to && from !== to && edges.has(`${from}>${to}`);
  const rows = trace?.rows || [];
  const byId = new Map(rows.map((r) => [r.id, r]));
  const frontends = (map?.nodes || []).filter((n) => n.inBrowser).map((n) => n.id);
  const hops = [];
  const add = (from, to, bad) => {
    const edgeId = `${from}>${to}`;
    if (!hops.some((h) => h.edgeId === edgeId)) { hops.push({ edgeId, from, to, bad }); }
  };
  rows.forEach((row, index) => {
    const bad = row.status === 'error';
    if (!row.parentId && index === 0) {
      if (has('browser', row.node)) { add('browser', row.node, bad); }
      return;
    }
    const before = rows.slice(0, index).reverse();
    const parent = byId.get(row.parentId);
    const candidates = [
      ...(row.kind === 'consumer' ? before.filter((r) => r.nodeKind === 'topic') : []),
      ...(parent ? [parent] : []),
      ...before,
    ];
    const source = candidates.find((r) => has(r.node, row.node));
    if (source) { add(source.node, row.node, bad); return; }
    // A browser request starts at the frontend whose page made it (its node is `inBrowser`), not at a Browser box.
    const front = candidates.some((r) => r.node === 'browser') ? frontends.find((id) => has(id, row.node)) : null;
    if (front) { add(front, row.node, bad); }
  });
  return hops;
}

export const emptyFlights = () => ({ active: [], waiting: [] });

/** New flights (`[{ key, hops }]`): flying if there is room, else waiting if there is room, else dropped. */
export function enqueueFlights(state, flights, { maxActive = MAX_FLIGHTS, maxWaiting = MAX_WAITING } = {}) {
  const known = new Set([...state.active, ...state.waiting].map((f) => f.key));
  const active = [...state.active];
  const waiting = [...state.waiting];
  flights.filter((f) => f.hops?.length && !known.has(f.key)).forEach((f) => {
    if (active.length < maxActive) { active.push({ ...f, at: 0 }); } else if (waiting.length < maxWaiting) { waiting.push(f); }
  });
  return { active, waiting };
}

/** One hop on: every flight moves to its next arrow; a finished one lands and a waiting one takes off. */
export function tickFlights(state, { maxActive = MAX_FLIGHTS } = {}) {
  const active = state.active.map((f) => ({ ...f, at: f.at + 1 })).filter((f) => f.at < f.hops.length);
  const waiting = [...state.waiting];
  while (active.length < maxActive && waiting.length) { active.push({ ...waiting.shift(), at: 0 }); }
  return { active, waiting };
}
